import { Maybe } from '@passionware/monads';
import {
  EnumFilter,
  enumFilterSchema
} from '../_common/query/filters/EnumFilter';
import { PaginatedResponse } from '../_common/query/pagination';
import {
  withBuilderUtils,
  withFiltersUtils,
  WithFilters,
  WithPagination,
  withPaginationUtils,
  WithSearch,
  withSearchUtils,
  WithSorter,
  withSorterUtils
} from '../_common/query/queryUtils';
import { z } from 'zod';

/** Entity type for reconciliation line items and financial documents. */
export type ReconciliationEntityType = 'booking' | 'expense';

/** Expense info for receipts (from reconciliation line item expenses). */
export interface FinancialDocumentExpenseInfo {
  expenseId: number;
  name: string | null;
  categoryName: string | null;
  vendor: string | null;
}

/** Common fields for all financial documents. */
interface FinancialDocumentBase {
  id: number | string;
  entity_id: number;
  document_data: {
    amount: number;
    currency?: string;
    reference?: string;
  };
  file_id?: number;
  /** File from backend: id, name, url (S3 or pre-signed). May include file_id for legacy compatibility. */
  file?: {
    id: number;
    name?: string;
    url: string | null;
    file_id?: number;
  };
  /** Document status: store ID or string (e.g. PAID, AWAITING_APPROVAL). */
  status?: number | string;
}

/** Invoice document (booking). */
export interface FinancialDocumentInvoice extends FinancialDocumentBase {
  type: 'booking';
}

/** Receipt document (expense); may include expense name/category. */
export interface FinancialDocumentReceipt extends FinancialDocumentBase {
  type: 'expense';
  expenseInfo: FinancialDocumentExpenseInfo;
}

/** Invoice or receipt document (discriminated by type). */
export type FinancialDocument =
  | FinancialDocumentInvoice
  | FinancialDocumentReceipt;

/** Computed status for the financial documents block: reconciled (actual = committed, all non–awaiting Paid/Approved), under, or over. */
export type DocumentsStatus = 'reconciled' | 'under' | 'over';

const RECONCILED_STATUSES: readonly string[] = ['PAID', 'APPROVED'];
const AWAITING_APPROVAL_STATUS = 'AWAITING_APPROVAL';

function normalizedDocumentStatus(status: number | string | undefined): string {
  return String(status ?? '').toUpperCase();
}

/**
 * Computes documents status for a line item: reconciled when actual = committed and all
 * non–awaiting-approval documents are Paid/Approved; under when actual < committed; over when actual > committed.
 */
export function computeDocumentsStatus(
  documents: FinancialDocument[],
  actual: number,
  committed: number
): DocumentsStatus | null {
  if (documents.length === 0) return null;
  if (actual < committed) return 'under';
  if (actual > committed) return 'over';
  const nonAwaiting = documents.filter(
    doc => normalizedDocumentStatus(doc.status) !== AWAITING_APPROVAL_STATUS
  );
  if (nonAwaiting.length === 0) return null;
  const allReconciled = nonAwaiting.every(doc =>
    RECONCILED_STATUSES.includes(normalizedDocumentStatus(doc.status))
  );
  return allReconciled ? 'reconciled' : null;
}

/** Linked expense on a booking line item (for adding receipts). */
export interface ReconciliationLineItemExpense {
  expenseId: number;
  name: Maybe<string>;
  categoryName: Maybe<string>;
  vendor: Maybe<string>;
  receiptIds: number[];
}

/** Common fields for all reconciliation line items. */
interface ReconciliationLineItemBase {
  id: number;
  /** Unique per row for bookings with multiple assignments; null for expenses. */
  assignmentId: number | null;
  updatedAt?: string | null;
  currency: string;
  /** Polymorphic display: vendor/role/organisation mapped from backend in adapter. */
  vendor: string | null;
  role: string | null;
  organisation?: string | null;
  production: {
    id: number;
    code: string | null;
    title: string | null;
    ownerId: string | null;
    color?: string | null;
  };
  budgeted: {
    committed: number;
    actual: number;
  };
  financialDocuments: FinancialDocument[];
  /** Computed in adapter: reconciled / under / over based on actual vs committed and document statuses. */
  documentsStatus: DocumentsStatus | null;
}

/** Booking line item: has booking id, resource (contact id + status), and linked expenses. */
export interface ReconciliationLineItemBooking
  extends ReconciliationLineItemBase {
  type: 'booking';
  bookingId: number;
  expenseId: null;
  productionBudgetExpenseId: null;
  /** Booking-only: contact id and booking status (vendor/role/org are on base). */
  resource: {
    id: number | null;
    resourceTypeId: number | null;
    pronounsOptionId: number | null;
    booking_status_id: number | null;
  };
  /** Linked expenses with details and receipt ids. */
  expenses: ReconciliationLineItemExpense[];
}

/** Expense line item: has expense id; no resource. */
export interface ReconciliationExpenseLineItem
  extends ReconciliationLineItemBase {
  type: 'expense';
  bookingId: null;
  expenseId: number;
  productionBudgetExpenseId?: number | null;
  /** Empty for expense line items. */
  expenses: ReconciliationLineItemExpense[];
}

/** Reconciliation line item (discriminated by type). */
export type ReconciliationLineItem =
  | ReconciliationLineItemBooking
  | ReconciliationExpenseLineItem;

/**
 * Stable list row id when multiple lines may share the same API
 * {@link ReconciliationLineItemBase.id}. Pass to {@link rowLevelValidation.mapToValidatedRows}
 * as `getRowId` while keeping {@link ReconciliationLineItem} as `data`.
 */
export function reconciliationLineListId(item: ReconciliationLineItem): string {
  return `${item.id}-${item.production.id}-${item.assignmentId}-${item.productionBudgetExpenseId}`;
}

/** Filter by document (invoice/receipt) status. API accepts single value as eq; toolbar uses EnumFilter. */
export interface ReconciliationFilters extends Record<string, unknown> {
  invoice_status: EnumFilter<string> | null;
  production_id: EnumFilter<number> | null;
  resource_id: EnumFilter<number> | null;
}

export const RECONCILIATION_SORT_FIELDS = [
  'updated_at',
  'booking_status'
] as const;

export type ReconciliationSortField =
  (typeof RECONCILIATION_SORT_FIELDS)[number];

export type ReconciliationTableColumnField =
  | ReconciliationSortField
  | 'production.title'
  | 'production.code'
  | 'production.ownerId'
  | 'role'
  | 'vendor';

export type ReconciliationSearchQuery = WithSearch &
  WithPagination &
  WithSorter<ReconciliationSortField> &
  WithFilters<ReconciliationFilters>;

export type ReconciliationQuery = ReconciliationSearchQuery;

export type ReconciliationListResponse =
  PaginatedResponse<ReconciliationLineItem>;

export interface ReconciliationApi {
  getList: (
    query: ReconciliationQuery,
    signal?: AbortSignal
  ) => Promise<ReconciliationListResponse>;
}

/** Page size is fixed at 50 by the API. */
export const RECONCILIATION_PAGE_SIZE = 50;

export const reconciliationQueryUtils = withBuilderUtils({
  ...withSearchUtils<ReconciliationSearchQuery>(),
  ...withFiltersUtils<ReconciliationSearchQuery>(),
  ...withPaginationUtils<ReconciliationSearchQuery>(),
  ...withSorterUtils<ReconciliationSearchQuery>(),
  ofEmptySearchQuery: (): ReconciliationSearchQuery => ({
    search: '',
    sort: null,
    page: { page: 0, pageSize: RECONCILIATION_PAGE_SIZE },
    filters: { invoice_status: null, production_id: null, resource_id: null }
  }),
  ofDefault: (): ReconciliationSearchQuery => ({
    search: '',
    sort: { field: 'updated_at', order: 'desc' },
    page: { page: 0, pageSize: RECONCILIATION_PAGE_SIZE },
    filters: { invoice_status: null, production_id: null, resource_id: null }
  }),
  matchesQuery: (
    query: ReconciliationSearchQuery,
    item: ReconciliationLineItem
  ) => {
    if (query.search) {
      const searchLower = query.search.toLowerCase();
      const matchesProduction =
        item.production.title?.toLowerCase().includes(searchLower) ||
        item.production.code?.toLowerCase().includes(searchLower);
      const matchesResource =
        item.role?.toLowerCase().includes(searchLower) ||
        item.vendor?.toLowerCase().includes(searchLower) ||
        item.organisation?.toLowerCase().includes(searchLower);
      if (!matchesProduction && !matchesResource) {
        return false;
      }
    }
    return true;
  }
}).setInitialQueryFactory(x => x.ofEmptySearchQuery);

const strToNull = (str: unknown) => (str === '' ? null : str);

const reconciliationPaginationSchema = z
  .object({
    page: z.coerce.number(),
    pageSize: z.coerce.number().transform(() => RECONCILIATION_PAGE_SIZE)
  })
  .catch(() => ({ page: 0, pageSize: RECONCILIATION_PAGE_SIZE }));

export const reconciliationSearchQuerySchema = z
  .object({
    search: z.preprocess(value => value || '', z.string()).default(''),
    filters: z.object({
      invoice_status: z
        .preprocess(strToNull, enumFilterSchema(z.string()).nullable())
        .prefault(null),
      production_id: z
        .preprocess(strToNull, enumFilterSchema(z.coerce.number()).nullable())
        .prefault(null),
      resource_id: z
        .preprocess(strToNull, enumFilterSchema(z.coerce.number()).nullable())
        .prefault(null)
    }),
    page: reconciliationPaginationSchema,
    sort: z
      .preprocess(
        strToNull,
        z
          .object({
            field: z.enum(RECONCILIATION_SORT_FIELDS),
            order: z.enum(['asc', 'desc'])
          })
          .nullable()
      )
      .default(null)
  })
  .catch(e => {
    console.error('Error parsing reconciliation search query', e.issues);
    return reconciliationQueryUtils.ofEmptySearchQuery();
  });

export const reconciliationQuerySchema = reconciliationSearchQuerySchema;
